import { MAX_DESCRIPTION_LENGTH } from '../useAppraisalFlow'
import useTranslation from 'next-translate/useTranslation'
import { useState } from 'react'
import { Button } from '@/components/Button'
import { cn } from '@/utils/cn'

/** From this many remaining characters on, the counter turns red. */
const COUNTER_WARNING_REMAINING = 20

const EXAMPLE_KEYS = [
  'example_1',
  'example_2',
  'example_3',
  'example_4',
  'example_5',
] as const

export interface AppraisalLandingProps {
  onSubmit: (description: string) => void
}

export default function AppraisalLanding({ onSubmit }: AppraisalLandingProps) {
  const { t } = useTranslation('wertCheck')
  const [description, setDescription] = useState('')

  const trimmed = description.trim()

  return (
    <form
      className="flex flex-col gap-6"
      onSubmit={(event) => {
        event.preventDefault()

        if (trimmed) {
          onSubmit(trimmed)
        }
      }}
    >
      <h1 className="text-heading-m text-primary text-center">
        {t('headline')}
      </h1>

      <div className="bg-static-white rounded-xl shadow-[0_0_2rem_rgba(0,0,0,0.1)] p-4">
        <textarea
          className="input-border w-full rounded-xl p-4 text-l text-primary resize-none min-h-[7rem]"
          placeholder={t('input_placeholder')}
          value={description}
          maxLength={MAX_DESCRIPTION_LENGTH}
          rows={3}
          onChange={(event) => setDescription(event.target.value)}
          onKeyDown={(event) => {
            // Enter starts the valuation; Shift+Enter inserts a line break
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault()
              if (trimmed) {
                onSubmit(trimmed)
              }
            }
          }}
        />

        <p
          className={cn(
            'text-s text-right tabular-nums',
            description.length >
              MAX_DESCRIPTION_LENGTH - COUNTER_WARNING_REMAINING
              ? 'text-negative'
              : 'text-secondary',
          )}
        >
          {description.length}/{MAX_DESCRIPTION_LENGTH}
        </p>

        <div className="flex flex-wrap gap-2 mt-3">
          {EXAMPLE_KEYS.map((key) => (
            <button
              key={key}
              type="button"
              className="input-border rounded-full bg-static-white px-4 py-2 text-s text-primary"
              onClick={() => {
                setDescription(t(key))
                onSubmit(t(key))
              }}
            >
              {t(key)}
            </button>
          ))}
        </div>
      </div>

      <Button
        type="submit"
        variant="cta"
        className="w-full"
        disabled={!trimmed}
      >
        {t('submit_description')}
      </Button>
    </form>
  )
}
